import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $createHeadingNode, $createQuoteNode, HeadingTagType } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import { ProLightBlockQuote } from '@nexelem/icons/ProLightBlockQuote';
import { ProLightH1 } from '@nexelem/icons/ProLightH1';
import { ProLightH2 } from '@nexelem/icons/ProLightH2';
import { ProLightH3 } from '@nexelem/icons/ProLightH3';
import { ProLightListCheck } from '@nexelem/icons/ProLightListCheck';
import { ProLightListOl } from '@nexelem/icons/ProLightListOl';
import { ProLightListUl } from '@nexelem/icons/ProLightListUl';
import { ProLightParagraph } from '@nexelem/icons/ProLightParagraph';
import { Button } from '@nexelem/components';
import { getTranslation } from '@nexelem/utils';
import { TFunction } from 'i18next';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  DEPRECATED_$isGridSelection,
  LexicalEditor,
} from 'lexical';
import { useMemo, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButton } from '../ActionButton';
import { ButtonWithMenu } from '../ButtonWithMenu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBlockQuote,
  faH1,
  faH2,
  faH3,
  faListCheck,
  faListOl,
  faListUl,
  faParagraph,
} from '@fortawesome/pro-light-svg-icons';

const BASE_ACCESSOR = 'components.richTextEditor.blockTypes';

export const getBlockTypeToBlockName = (translation: TFunction) => {
  const t = getTranslation(translation, BASE_ACCESSOR);
  return {
    bullet: {
      text: t('bulledList'),
      Icon: ProLightListUl,
    },
    check: {
      text: t('checkList'),
      Icon: ProLightListCheck,
    },
    h1: {
      text: t('header1'),
      Icon: ProLightH1,
    },
    h2: {
      text: t('header2'),
      Icon: ProLightH2,
    },
    h3: {
      text: t('header3'),
      Icon: ProLightH3,
    },
    number: {
      text: t('numberedList'),
      Icon: ProLightListOl,
    },
    paragraph: {
      text: t('paragraph'),
      Icon: ProLightParagraph,
    },
    quote: {
      text: t('quote'),
      Icon: ProLightBlockQuote,
    },
  };
};
export const rootTypeToRootName = {
  root: 'Root',
  table: 'Table',
};

type BlockFormatDropDownProps = {
  blockType: keyof ReturnType<typeof getBlockTypeToBlockName>;
  editor: LexicalEditor;
  disabled?: boolean;
};

export function BlockFormatDropDown({ editor, blockType, disabled = false }: BlockFormatDropDownProps): JSX.Element {
  const { t: translation } = useTranslation();
  const t = getTranslation(translation, BASE_ACCESSOR);
  const blockTypeToBlockName = useMemo(() => getBlockTypeToBlockName(translation), [translation]);

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) || DEPRECATED_$isGridSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatHeading = (headingSize: HeadingTagType) => () => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) || DEPRECATED_$isGridSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode(headingSize));
        }
      });
    }
  };

  const formatBulletList = () => {
    if (blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatCheckList = () => {
    if (blockType !== 'check') {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatNumberedList = () => {
    if (blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) || DEPRECATED_$isGridSelection(selection)) {
          $setBlocksType(selection, () => $createQuoteNode());
        }
      });
    }
  };

  const SelectedElement = blockTypeToBlockName[blockType];
  return (
    <ButtonWithMenu
      disabled={disabled}
      content={() => (
        <>
          <ActionButton
            Icon={<FontAwesomeIcon icon={faParagraph} />}
            isActive={blockType === 'paragraph'}
            label={t('paragraph')}
            onClick={formatParagraph}
          />
          <ActionButton
            Icon={<FontAwesomeIcon icon={faH1} />}
            isActive={blockType === 'h1'}
            label={t('header1')}
            onClick={formatHeading('h1')}
          />
          <ActionButton
            Icon={<FontAwesomeIcon icon={faH2} />}
            isActive={blockType === 'h2'}
            label={t('header2')}
            onClick={formatHeading('h2')}
          />
          <ActionButton
            Icon={<FontAwesomeIcon icon={faH3} />}
            isActive={blockType === 'h3'}
            label={t('header3')}
            onClick={formatHeading('h3')}
          />
          <ActionButton
            Icon={<FontAwesomeIcon icon={faListUl} />}
            isActive={blockType === 'bullet'}
            label={t('bulledList')}
            onClick={formatBulletList}
          />
          <ActionButton
            Icon={<FontAwesomeIcon icon={faListOl} />}
            isActive={blockType === 'number'}
            label={t('numberedList')}
            onClick={formatNumberedList}
          />
          <ActionButton
            Icon={<FontAwesomeIcon icon={faListCheck} />}
            isActive={blockType === 'check'}
            label={t('checkList')}
            onClick={formatCheckList}
          />
          <ActionButton
            Icon={<FontAwesomeIcon icon={faBlockQuote} />}
            isActive={blockType === 'quote'}
            label={t('quote')}
            onClick={formatQuote}
          />
        </>
      )}
      openingElement={
        <Button disabled={disabled} StartIcon={<SelectedElement.Icon />} variant="text">
          {SelectedElement.text}
        </Button>
      }
    />
  );
}
